import { useEffect, useState } from 'react'
import { connect, failureText, inbox, providers, type BrandedConnection, type ProviderCard } from './api'
import { store } from './store'
import { Tile, tones } from './visuals'

/** Adding mail accounts: the whole onboarding, and Settings → Accounts. */
export function Connect({ firstRun, onDone, googleEmail }: { firstRun: boolean; onDone?: () => void; googleEmail?: string }) {
  const [cards, setCards] = useState<ProviderCard[]>([])
  const [alternatives, setAlternatives] = useState<ProviderCard[]>([])
  const [connections, setConnections] = useState<BrandedConnection[]>([])
  const [openCard, setOpenCard] = useState<string | null>(null)
  const [fields, setFields] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const reload = async () => {
    const result = await providers()
    setCards(result.cards); setAlternatives(result.alternatives); setConnections(result.connections)
    return result
  }
  useEffect(() => {
    void reload()
    const params = new URLSearchParams(location.search)
    if (params.get('connection') === 'connected') setNotice('Gmail is connected. Your mail is on its way.')
    if (params.get('connection') === 'failed') setError('Google didn’t finish connecting. Try again.')
    if (params.has('connection')) history.replaceState(null, '', '/')
  }, [])

  const card = [...cards, ...alternatives].find(item => item.id === openCard)

  async function start(target: ProviderCard) {
    setError(''); setNotice('')
    if (!target.ready) return
    if (target.kind === 'oauth') {
      setBusy(true)
      try {
        const result = await connect(target.id)
        if (result.authorizeUrl) location.assign(result.authorizeUrl)
      } catch (failure) { setError(failureText(failure)); setBusy(false) }
      return
    }
    setFields(target.id === 'imap:gmail' && googleEmail ? { email: googleEmail } : {})
    setOpenCard(target.id)
  }

  async function submit() {
    if (!card) return
    setBusy(true); setError('')
    try {
      const result = await connect(card.id, fields)
      if (result.connectionId) {
        setOpenCard(null); setFields({})
        setNotice(`${card.name} is connected. Your mail is on its way.`)
        await reload()
        store.soon(100)
      }
    } catch (failure) { setError(failureText(failure)) } finally { setBusy(false) }
  }

  async function disconnect(connection: BrandedConnection) {
    if (!confirm(`Disconnect ${connection.name}? Its mail disappears from Minimal Mail; nothing is deleted from the provider.`)) return
    try { await inbox.disconnectConnection(connection.id); await reload(); store.soon(100) } catch (failure) { setError(failureText(failure)) }
  }

  const live = connections.filter(connection => connection.status !== 'disconnected')
  // Same colour order as the inbox: by when each was connected.
  const liveTones = tones([...live].sort((a, b) => a.createdAt.localeCompare(b.createdAt)))
  return (
    <div className="connect">
      <div className="connect-col view-in">
        {firstRun && <>
          <h1 className="connect-title">Bring in your mail</h1>
          <p className="muted">Connect each account once. Minimal Mail reads them together and puts what needs you first.</p>
        </>}
        {live.length > 0 && (
          <section className="connect-section">
            <h3>Connected</h3>
            {live.map(connection => (
              <div key={connection.id} className="connected">
                <Tile brand={connection.brand} tone={liveTones.get(connection.id) ?? 0} size={22} />
                <span className="connected-name">{connection.name}</span>
                <span className={`small ${connection.status === 'connected' ? 'faint' : 'warn'}`}>{connection.status === 'connected' ? 'Connected' : 'Reconnect needed'}</span>
                <button className="link small" onClick={() => void disconnect(connection)}>Disconnect</button>
              </div>
            ))}
          </section>
        )}
        <section className="connect-section">
          <h3>{live.length ? 'Add another' : 'Choose a provider'}</h3>
          {cards.map(item => (
            <div key={item.id} className={`provider${openCard === item.id || (item.alternative && openCard === item.alternative.card) ? ' open' : ''}${item.ready ? '' : ' disabled'}`}>
              <button className="provider-row" onClick={() => void start(item)} disabled={!item.ready || busy}>
                <Tile brand={item.brand} size={26} />
                <span className="provider-name">{item.name}</span>
                <span className="faint small">{item.ready ? item.summary : item.unavailable}</span>
                {item.ready && <span className="provider-action">{item.kind === 'oauth' ? item.actionLabel : 'Connect'} →</span>}
              </button>
              {item.alternative && openCard !== item.alternative.card && (
                <button className="link small alt" onClick={() => { const alt = alternatives.find(a => a.id === item.alternative!.card); if (alt) void start(alt) }}>{item.alternative.label}</button>
              )}
              {card && (openCard === item.id || item.alternative?.card === openCard) && card.kind === 'password' && (
                <form className="provider-form" onSubmit={event => { event.preventDefault(); void submit() }}>
                  {card.fields?.map((field, index) => (
                    <label key={field.name} className="input-label">
                      <span>{field.label}</span>
                      <input type={field.type} value={fields[field.name] ?? ''} placeholder={field.placeholder} autoComplete={field.autocomplete} autoFocus={index === (fields.email ? 1 : 0)}
                        onChange={event => setFields(previous => ({ ...previous, [field.name]: event.target.value }))} />
                    </label>
                  ))}
                  {card.help && <p className="faint small">{card.help.text} <a href={card.help.url} target="_blank" rel="noopener noreferrer">{card.help.linkLabel}</a></p>}
                  <div className="form-actions">
                    <button className="primary" disabled={busy} type="submit">{busy ? 'Connecting…' : `Connect ${card.name}`}</button>
                    <button className="link" type="button" onClick={() => setOpenCard(null)}>Cancel</button>
                  </div>
                </form>
              )}
            </div>
          ))}
        </section>
        {error && <p className="warn">{error}</p>}
        {notice && <p className="ok">{notice}</p>}
        {firstRun && live.length > 0 && <button className="primary wide" onClick={onDone}>Open my inbox →</button>}
      </div>
    </div>
  )
}
